import React from 'react';
import { Shield, Star } from 'lucide-react';

interface ProfileCompletionTrackerProps {
  user: any;
}

const ProfileCompletionTracker: React.FC<ProfileCompletionTrackerProps> = ({ user }) => {
  const requiredFields = [
    'fullName',
    'phone',
    'occupation',
    'location',
    'languages'
  ];

  const completedFields = requiredFields.filter(field => {
    if (field === 'languages') {
      return user[field]?.length > 0;
    }
    return !!user[field];
  });

  const completionPercentage = (completedFields.length / requiredFields.length) * 100;

  const getVerificationStatus = () => {
    const idVerified = user.verifications?.id?.status === 'verified';
    const addressVerified = user.verifications?.address?.status === 'verified';

    if (idVerified && addressVerified) {
      return 'host';
    } else if (idVerified) {
      return 'basic';
    }
    return 'none';
  };

  const verificationStatus = getVerificationStatus();

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Profile Completion</h2>
          <p className="text-sm text-gray-500">
            {completionPercentage < 100 
              ? 'Complete your profile to unlock all features'
              : 'Your profile is complete!'}
          </p>
        </div>
        {verificationStatus !== 'none' && (
          <div className={`flex items-center px-3 py-1 rounded-full ${
            verificationStatus === 'host' 
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {verificationStatus === 'host' ? (
              <>
                <Star className="h-4 w-4 mr-1.5" />
                <span className="text-sm font-medium">Host Verified</span>
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-1.5" />
                <span className="text-sm font-medium">Basic Verified</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="relative">
        <div className="flex mb-2 items-center justify-between">
          <div>
            <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-100">
              {completionPercentage.toFixed(0)}% Complete
            </span>
          </div>
          <div className="text-right">
            <span className="text-xs font-semibold inline-block text-indigo-600">
              {completedFields.length}/{requiredFields.length} Required Fields
            </span>
          </div>
        </div>
        <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-100">
          <div
            style={{ width: `${completionPercentage}%` }}
            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600 transition-all duration-500"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionTracker;